import React, { useEffect } from 'react';
import { View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import useResponsiveness from '@/hooks/useResponsiveness';

let shimmerInjected = false;
const injectShimmer = () => {
  if (Platform.OS !== 'web' || shimmerInjected) return;
  shimmerInjected = true;
  if (document.getElementById('guide-skeleton-styles')) return;
  const style = document.createElement('style');
  style.id = 'guide-skeleton-styles';
  style.textContent = `
    @keyframes guideSkeletonPulse {
      0%, 100% { opacity: 0.5; }
      50% { opacity: 0.9; }
    }
    .guide-skeleton-block { animation: guideSkeletonPulse 1.4s ease-in-out infinite; }
  `;
  document.head.appendChild(style);
};

/**
 * Loading placeholder shown while a guide page is fetched from the API.
 * Mirrors the DocsHeader + content rhythm so there's no layout shift.
 */
export default function GuidePageSkeleton() {
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const isDark = isDarkTheme(theme);

  useEffect(() => { injectShimmer(); }, []);

  const base = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';

  const Bar = ({ width, height = 16, radius = 8, mb = 12 }) => (
    <View
      className="guide-skeleton-block"
      style={{
        width,
        height,
        borderRadius: radius,
        backgroundColor: base,
        marginBottom: mb,
      }}
    />
  );

  return (
    <View accessibilityLabel="Loading guide" style={{ width: '100%' }}>
      {/* Badge */}
      <Bar width={120} height={28} radius={100} mb={20} />
      {/* Title */}
      <Bar width="80%" height={isXS ? 34 : 44} radius={10} mb={16} />
      {/* Description */}
      <Bar width="95%" height={20} mb={8} />
      <Bar width="70%" height={20} mb={40} />

      {/* Overview card */}
      <View
        style={{
          padding: 24,
          borderRadius: 16,
          backgroundColor: isDark ? 'rgba(59,130,246,0.05)' : 'rgba(59,130,246,0.03)',
          marginBottom: 40,
        }}
      >
        <Bar width={180} height={18} mb={16} />
        <Bar width="90%" height={14} mb={10} />
        <Bar width="85%" height={14} mb={10} />
        <Bar width="60%" height={14} mb={0} />
      </View>

      {/* Section blocks */}
      {[0, 1, 2].map((i) => (
        <View key={i} style={{ marginBottom: isXS ? 40 : 56 }}>
          <Bar width="50%" height={isXS ? 24 : 28} radius={8} mb={20} />
          <Bar width="100%" height={14} mb={10} />
          <Bar width="96%" height={14} mb={10} />
          <Bar width="88%" height={14} mb={0} />
        </View>
      ))}
    </View>
  );
}
